<script module lang="ts">
    export interface RaiseHandSpaceEntry {
        spaceName: string;
        kind: string;
        label: string;
        raised: boolean;
    }
</script>

<script lang="ts">
    import { onMount } from "svelte";
    import { clickOutside } from "svelte-outside";
    import { LL } from "../../../i18n/i18n-svelte";
    import RaiseHandIcon from "../Icons/meta/RaiseHandIcon.svelte";

    interface Props {
        entries: RaiseHandSpaceEntry[];
        onselect?: (entry: RaiseHandSpaceEntry) => void;
        onclose?: () => void;
    }

    let { entries = [], onselect, onclose }: Props = $props();

    const titleId = "raise-hand-space-picker-title";
    let container: HTMLDivElement | undefined = $state();
    // The element that opened the picker (the raise-hand button): keyboard focus goes back to it on close.
    let opener: HTMLElement | undefined;

    onMount(() => {
        opener = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
        container?.querySelector("button")?.focus();
    });

    function closeFromKeyboardOrChoice(): void {
        opener?.focus();
        onclose?.();
    }

    function handleSelect(entry: RaiseHandSpaceEntry): void {
        onselect?.(entry);
        closeFromKeyboardOrChoice();
    }

    function onKeyDown(event: KeyboardEvent): void {
        if (event.key === "Escape") {
            event.preventDefault();
            closeFromKeyboardOrChoice();
        }
    }
</script>

<svelte:window onkeydown={onKeyDown} />

<div
    bind:this={container}
    role="group"
    aria-labelledby={titleId}
    data-testid="raise-hand-space-picker"
    class="bg-mlight/80 backdrop-blur-md rounded-md shadow-light p-2 max-w-96 overflow-auto flex flex-col gap-1"
    use:clickOutside={() => onclose?.()}
>
    <div id={titleId} class="text-sm font-semibold text-mdark px-2 pb-1">
        {$LL.actionbar.help.raiseHand.title()}
    </div>

    {#each entries as entry (entry.spaceName)}
        <button
            type="button"
            class="w-full text-left p-2 rounded transition-colors flex flex-row items-center gap-2 {entry.raised
                ? 'bg-mblue/10 hover:bg-mblue/20'
                : 'hover:bg-mdark/10'}"
            data-testid="raise-hand-space-option-{entry.kind}"
            aria-pressed={entry.raised}
            onclick={() => handleSelect(entry)}
        >
            <RaiseHandIcon
                height="h-5"
                width="w-5"
                strokeColor={entry.raised ? "stroke-mblue fill-transparent" : "stroke-mdark fill-transparent"}
                hover=""
            />
            <span class="text-sm text-mdark flex-1 truncate" title={entry.label}>{entry.label}</span>
        </button>
    {/each}
</div>
